/**
 * MeseView — vista Mese del calendario. Grid 7-colonne con header giorni,
 * numero settimana cliccabile (→ /periodo/settimana/...), 4-6 righe di
 * settimane con celle giorno contenenti:
 *   - numero giorno (oggi evidenziato)
 *   - heatmap di sfondo (% MIT fatte)
 *   - lista MIT (max 4) draggable + click-to-edit via popover
 *   - lista Azioni con data specifica (max 3)
 *   - badge scadenze KR/tappa
 *   - bottone quick-add MIT/Azione hover
 *
 * Drag&Drop: trascinando una MIT su un'altra cella si invoca `onReassign(id, currentData, newData)`.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { GIORNI_FULL, heatmapBg } from '../../lib/calendarConstants';
import { formatRangeSettimana } from '../../lib/weekFormat';
import Checkbox from '../nav/Checkbox';
import DateAssignPopover from '../nav/DateAssignPopover';

export default function MeseView({
  data, todayIso, onToggleMit, onToggleAzione, onReassignMit,
}) {
  const navigate = useNavigate();
  const [dragOverDay, setDragOverDay] = useState(null);

  return (
    <>
      <div className="mb-3 font-mono text-[10px] text-mute">
        Trascina una MIT su un altro giorno per spostarla, oppure clic sul testo per aprire il selettore data.
      </div>
      <div className="border-l border-t border-line" data-testid="cal-grid">
        {/* Header giorni della settimana */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)]">
          <div className="border-r border-b border-line py-2 px-2 font-mono text-[10px] uppercase tracking-[0.15em] text-mute">W</div>
          {GIORNI_FULL.map((g, i) => (
            <div
              key={`gh-${i}-${g}`}
              className={'border-r border-b border-line py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] ' + (i >= 5 ? 'text-mute' : 'text-soft')}
            >
              {g}
            </div>
          ))}
        </div>

        {/* Settimane */}
        {data.weeks.map((w) => (
          <WeekRow
            key={w.settimana}
            week={w}
            todayIso={todayIso}
            dragOverDay={dragOverDay}
            setDragOverDay={setDragOverDay}
            onReassignMit={onReassignMit}
            onToggleMit={onToggleMit}
            onToggleAzione={onToggleAzione}
            navigate={navigate}
          />
        ))}
      </div>

      <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-soft">
        {data.totali?.mit || 0} MIT · {data.totali?.scadenze || 0} scadenze nel mese (incluse settimane di bordo).
      </div>
    </>
  );
}

function WeekRow({ week, todayIso, dragOverDay, setDragOverDay, onReassignMit, onToggleMit, onToggleAzione, navigate }) {
  const wNum = week.settimana.split('-W')[1];
  return (
    <div className="grid grid-cols-[60px_repeat(7,1fr)]">
      {/* Numero settimana */}
      <Link
        to={`/periodo/settimana/${week.settimana}`}
        className="border-r border-b border-line py-3 px-2 flex flex-col items-center justify-start font-mono text-[10px] text-mute hover:text-ink hover:bg-linesoft leading-tight"
        data-testid={`cal-week-${week.settimana}`}
        title={`W${wNum} | ${formatRangeSettimana(week.settimana)}`}
      >
        <span className="text-xs">{wNum}</span>
        <span className="text-[8px] text-mute mt-0.5">{formatRangeSettimana(week.settimana)}</span>
      </Link>
      {week.giorni.map((g) => (
        <DayCell
          key={g.data}
          giorno={g}
          isToday={g.data === todayIso}
          isDropTarget={dragOverDay === g.data}
          onDragEnter={() => setDragOverDay(g.data)}
          onDragLeave={() => setDragOverDay((d) => (d === g.data ? null : d))}
          onDrop={async (payload) => {
            setDragOverDay(null);
            try {
              const { id, currentData } = JSON.parse(payload);
              await onReassignMit(id, currentData, g.data);
            } catch {
              toast.error('Drop payload non valido');
            }
          }}
          onToggleMit={onToggleMit}
          onToggleAzione={onToggleAzione}
          onReassignMit={onReassignMit}
          onPickDay={() => navigate(`/periodo/giorno/${g.data}`)}
        />
      ))}
    </div>
  );
}

function DayCell({
  giorno: g, isToday, isDropTarget,
  onDragEnter, onDragLeave, onDrop,
  onToggleMit, onToggleAzione, onReassignMit, onPickDay,
}) {
  const pct = g.mit_totale > 0 ? g.mit_fatte / g.mit_totale : 0;
  const bg = heatmapBg(g.stato, pct);
  return (
    <div
      className={`group/day border-r border-b min-h-[120px] p-2 relative flex flex-col gap-1.5 transition-colors ${isDropTarget ? 'border-ok bg-ok/10' : 'border-line'}`}
      style={{ backgroundColor: isDropTarget ? undefined : bg, opacity: g.in_mese ? 1 : 0.4 }}
      data-testid={`cal-day-${g.data}`}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; onDragEnter(); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        const payload = e.dataTransfer.getData('application/x-qp-mit');
        if (payload) onDrop(payload);
      }}
    >
      <div className="flex items-center justify-between">
        <button
          onClick={onPickDay}
          className={
            'font-mono text-sm tabular-nums hover:underline ' +
            (isToday ? 'bg-ink text-canvas px-1.5 py-0.5' : 'text-ink')
          }
          data-testid={`cal-day-link-${g.data}`}
        >
          {g.giorno}
        </button>
        <div className="flex items-center gap-1.5">
          {(g.scadenze || []).length > 0 && (
            <span
              className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.15em] text-accent"
              title={g.scadenze.map((s) => s.titolo).join(' · ')}
              data-testid={`cal-scad-${g.data}`}
            >
              <AlertTriangle size={10} strokeWidth={1.5} /> {g.scadenze.length}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              window.dispatchEvent(new CustomEvent('qp:quick-add-open', {
                detail: { kind: 'mit', dataIso: g.data },
              }));
            }}
            className="opacity-0 group-hover/day:opacity-100 transition-opacity p-0.5 hover:bg-ink hover:text-canvas text-soft border border-line hover:border-ink"
            title={`Aggiungi MIT o azione · ${g.data}`}
            aria-label="Aggiungi MIT o azione"
            data-testid={`cal-day-add-${g.data}`}
          >
            <Plus size={11} strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* MIT list — draggable + click-to-edit via popover */}
      {(g.mit || []).slice(0, 4).map((m) => (
        <MitMiniRow key={m.id} mit={m} onToggleMit={onToggleMit} onReassignMit={onReassignMit} />
      ))}
      {g.mit_totale > 4 && (
        <span className="text-[9px] font-mono text-mute">+{g.mit_totale - 4} MIT</span>
      )}
      {/* Azioni con `data` specifica nel giorno */}
      {(g.azioni || []).slice(0, 3).map((a) => (
        <AzioneMiniRow key={a.id} azione={a} onToggleAzione={onToggleAzione} />
      ))}
      {(g.azioni || []).length > 3 && (
        <span className="text-[9px] font-mono text-mute">+{(g.azioni || []).length - 3} az</span>
      )}
    </div>
  );
}

function MitMiniRow({ mit: m, onToggleMit, onReassignMit }) {
  return (
    <div
      className="group flex items-start gap-1.5 text-[11px] leading-tight px-1 py-0.5 cursor-move hover:bg-paper/40 rounded transition-colors"
      data-testid={`cal-mit-${m.id}`}
      title={`${m.testo}\nTrascina su un altro giorno per spostare`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/x-qp-mit', JSON.stringify({ id: m.id, currentData: m.data }));
      }}
    >
      <Checkbox
        checked={m.fatto}
        onToggle={() => onToggleMit(m.id, m.fatto)}
        size={14}
        label={m.testo}
        testid={`cal-toggle-mit-${m.id}`}
      />
      <DateAssignPopover
        kind="mit"
        currentValue={m.data}
        onSave={(nv) => onReassignMit(m.id, m.data, nv)}
        side="right"
        testid={`cal-mese-mit-popover-${m.id}`}
      >
        <button
          type="button"
          className="flex-1 text-left"
          style={{
            textDecoration: m.fatto ? 'line-through' : 'none',
            color: m.fatto ? 'var(--qp-text-mute)' : 'var(--qp-ink)',
            opacity: m.fatto ? 0.5 : 1,
            transition: 'opacity 280ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
          data-testid={`cal-mese-mit-trigger-${m.id}`}
        >
          {m.testo}
        </button>
      </DateAssignPopover>
    </div>
  );
}

function AzioneMiniRow({ azione: a, onToggleAzione }) {
  return (
    <div
      className="flex items-start gap-1.5 text-[11px] leading-tight px-1 py-0.5 hover:bg-paper/40 rounded transition-colors border-l-2 border-soft"
      data-testid={`cal-mese-azione-${a.id}`}
      title={`Azione · ${a.testo}`}
    >
      <Checkbox
        checked={a.fatto}
        onToggle={() => onToggleAzione(a.id, a.fatto)}
        size={14}
        label={a.testo}
        testid={`cal-mese-toggle-azione-${a.id}`}
      />
      <span
        className="font-mono text-[8px] uppercase tracking-[0.15em] text-mute shrink-0 mt-0.5"
        aria-label="Azione"
      >
        AZ
      </span>
      <span
        className="flex-1 italic"
        style={{
          textDecoration: a.fatto ? 'line-through' : 'none',
          color: a.fatto ? 'var(--qp-text-mute)' : 'var(--qp-ink)',
          opacity: a.fatto ? 0.5 : 1,
        }}
      >
        {a.testo}
      </span>
    </div>
  );
}
